// apps/web/src/pages/cadastros/acessos-portal-page.tsx — Fase 17, Plano 13 (PORT-01; D-04, D-09, D-10, D-11; PORT-UI-20..23).
//
// Tela interna "Acessos do portal" (/cadastros/acessos-portal, permissão `portal:gerenciar-acessos`): a pessoa da
// Pieltec escolhe o cliente, lista os usuários do portal dele, convida um novo usuário, entrega o link de primeiro
// acesso ou de redefinição, desativa, reativa e revoga links. `AcessosPortalConteudo` é puro (testável com
// renderToStaticMarkup); `AcessosPortalPage` cuida de dados, URL (?clienteId=) e diálogos. O link gerado só existe
// no estado desta página enquanto o diálogo estiver aberto.
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { AlertCircle, Building2, CheckCircle2, Hourglass, KeyRound, Link2Off, Loader2, MoreHorizontal, UserCheck, UserPlus, UserX, type LucideIcon } from 'lucide-react';
import type {
  AcessoPortalLista,
  AcessoPortalView,
  ClienteLookupPv,
  CriarAcessoPortalResposta,
  FiltroSituacaoAcessoPortal,
  LinkPortalGerado,
  SituacaoAcessoPortal,
} from '@pieltec/contracts';
import { ClienteCombobox } from '../../components/vendas/cliente-combobox';
import { Button } from '../../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { TablePagination } from '../../components/ui/table-pagination';
import { useToast } from '../../components/ui/toast';
import {
  acoesDaLinha,
  buscarClientePorId,
  desativarAcesso,
  ErroAcessoPortal,
  FILTROS_SITUACAO,
  gerarLink,
  linkVivo,
  listarAcessos,
  MENSAGEM_ERRO_CARGA,
  MENSAGEM_SEM_PERMISSAO_TELA,
  mensagemErroAcesso,
  reativarAcesso,
  revogarLink,
  rotuloLinkAcesso,
  rotuloSituacaoAcesso,
  rotuloUltimoAcesso,
  ROTULO_FILTRO_SITUACAO,
  type AcaoConfirmavel,
  type ErroAcessoTraduzido,
  type IdAcaoAcesso,
} from '../../lib/portal-acessos';
import { AcessoConfirmarDialog, AcessoPortalDialog, LinkGeradoDialog } from './acessos-portal-dialogs';

const CABECALHO_COLUNA = 'px-4 py-2 text-[length:var(--font-label)] font-semibold text-muted-foreground';
const CLASSE_ALVO = 'min-h-11 lg:min-h-8';
const ATRASO_BUSCA_MS = 300;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ICONE_SITUACAO: Readonly<Record<SituacaoAcessoPortal, LucideIcon>> = {
  PENDENTE_PRIMEIRO_ACESSO: Hourglass,
  ATIVO: CheckCircle2,
  DESATIVADO: UserX,
};

const ICONE_ACAO: Readonly<Record<IdAcaoAcesso, LucideIcon>> = {
  'gerar-link': KeyRound,
  'gerar-novo-link': KeyRound,
  'revogar-link': Link2Off,
  desativar: UserX,
  reativar: UserCheck,
};

const TESTID_ACAO: Readonly<Record<IdAcaoAcesso, string>> = {
  'gerar-link': 'acessos-portal-gerar-link',
  'gerar-novo-link': 'acessos-portal-gerar-link',
  'revogar-link': 'acessos-portal-revogar-link',
  desativar: 'acessos-portal-desativar',
  reativar: 'acessos-portal-reativar',
};

export type ErroTelaAcessos = 'carga' | 'permissao';

function SeloSituacao({ situacao }: { readonly situacao: SituacaoAcessoPortal }): ReactElement {
  const Icone = ICONE_SITUACAO[situacao];
  const tom = situacao === 'DESATIVADO' ? 'text-muted-foreground' : 'text-foreground';
  return (
    <span
      data-testid="acessos-portal-situacao"
      className={`inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-1 text-[length:var(--font-label)] font-semibold ${tom}`}
    >
      <Icone aria-hidden="true" className="size-4 shrink-0" />
      {rotuloSituacaoAcesso(situacao)}
    </span>
  );
}

export interface AcessosPortalConteudoProps {
  readonly cliente: ClienteLookupPv | null;
  readonly busca?: string;
  readonly situacao?: FiltroSituacaoAcessoPortal;
  readonly lista: AcessoPortalLista | null;
  readonly carregando?: boolean;
  readonly erro?: ErroTelaAcessos | null;
  readonly agora?: Date;
  /** Usuário com uma ação em andamento (geração direta de link): seus botões ficam desabilitados. */
  readonly acaoEmAndamentoId?: string | null;
  readonly aoAcionar?: (acao: IdAcaoAcesso, acesso: AcessoPortalView) => void;
  readonly aoMudarCliente?: (cliente: ClienteLookupPv) => void;
  readonly aoMudarBusca?: (busca: string) => void;
  readonly aoMudarSituacao?: (situacao: FiltroSituacaoAcessoPortal) => void;
  readonly aoMudarPagina?: (pagina: number) => void;
  readonly aoConvidar?: () => void;
  readonly aoLimparFiltros?: () => void;
  readonly aoTentarNovamente?: () => void;
}

function BotaoConvidar({ desabilitado, aoConvidar }: { readonly desabilitado: boolean; readonly aoConvidar?: () => void }): ReactElement {
  return (
    <>
      <Button
        type="button"
        onClick={aoConvidar}
        disabled={desabilitado}
        aria-describedby={desabilitado ? 'acessos-portal-convidar-ajuda' : undefined}
        data-testid="acessos-portal-convidar"
        className={CLASSE_ALVO}
      >
        <UserPlus aria-hidden="true" className="size-4" />
        Convidar usuário
      </Button>
      {desabilitado ? (
        <span id="acessos-portal-convidar-ajuda" className="sr-only">
          Escolha um cliente para convidar um usuário.
        </span>
      ) : null}
    </>
  );
}

function EstadoPagina({
  icone: Icone,
  titulo,
  apoio,
  children,
}: {
  readonly icone: LucideIcon;
  readonly titulo: string;
  readonly apoio?: string;
  readonly children?: ReactElement;
}): ReactElement {
  return (
    <div role="status" className="flex flex-col items-center gap-4 py-12 text-center">
      <Icone aria-hidden="true" className="size-6 text-muted-foreground" />
      <div className="flex flex-col gap-2">
        <p className="text-[length:var(--font-heading)] font-semibold">{titulo}</p>
        {apoio ? <p className="text-muted-foreground">{apoio}</p> : null}
      </div>
      {children}
    </div>
  );
}

function ListaCarregando(): ReactElement {
  return (
    <div aria-busy="true" className="flex flex-col gap-4">
      <p className="sr-only">Carregando usuários…</p>
      {[0, 1, 2, 3, 4].map((indice) => (
        <Skeleton key={indice} className="h-12 w-full" />
      ))}
    </div>
  );
}

/** Ações sempre visíveis a partir de 1024 px; abaixo disso, menu "Ações" aberto por toque (nunca por hover). */
function AcoesLinha({
  acesso,
  agora,
  ocupado,
  aoAcionar,
}: {
  readonly acesso: AcessoPortalView;
  readonly agora: Date;
  readonly ocupado: boolean;
  readonly aoAcionar?: (acao: IdAcaoAcesso, acesso: AcessoPortalView) => void;
}): ReactElement {
  const acoes = acoesDaLinha(acesso, agora);
  return (
    <>
      <div className="hidden flex-wrap justify-end gap-2 lg:flex">
        {acoes.map((acao) => {
          const Icone = ICONE_ACAO[acao.id];
          const gerando = ocupado && (acao.id === 'gerar-link' || acao.id === 'gerar-novo-link');
          return (
            <Button
              key={acao.id}
              type="button"
              variant="outline"
              size="sm"
              disabled={ocupado}
              data-testid={TESTID_ACAO[acao.id]}
              onClick={() => aoAcionar?.(acao.id, acesso)}
              className={`${CLASSE_ALVO} ${acao.destrutiva ? 'text-destructive' : ''}`}
            >
              {gerando ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : <Icone aria-hidden="true" className="size-4" />}
              {acao.rotulo}
            </Button>
          );
        })}
      </div>
      <div className="flex justify-end lg:hidden">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm" disabled={ocupado} aria-label={`Ações para ${acesso.nome}`} className={CLASSE_ALVO}>
              {ocupado ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : <MoreHorizontal aria-hidden="true" className="size-4" />}
              Ações
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {acoes.map((acao) => {
              const Icone = ICONE_ACAO[acao.id];
              return (
                <DropdownMenuItem
                  key={acao.id}
                  variant={acao.destrutiva ? 'destructive' : 'default'}
                  data-testid={`${TESTID_ACAO[acao.id]}-menu`}
                  onSelect={() => aoAcionar?.(acao.id, acesso)}
                  className="min-h-11"
                >
                  <Icone aria-hidden="true" />
                  {acao.rotulo}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}

function TabelaAcessos({
  itens,
  agora,
  acaoEmAndamentoId,
  aoAcionar,
}: {
  readonly itens: readonly AcessoPortalView[];
  readonly agora: Date;
  readonly acaoEmAndamentoId: string | null;
  readonly aoAcionar?: (acao: IdAcaoAcesso, acesso: AcessoPortalView) => void;
}): ReactElement {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <table data-testid="acessos-portal-tabela" className="w-full text-left">
        <caption className="sr-only">Usuários do portal</caption>
        <thead>
          <tr>
            <th scope="col" className={CABECALHO_COLUNA}>Usuário</th>
            <th scope="col" className={CABECALHO_COLUNA}>Situação</th>
            <th scope="col" className={`${CABECALHO_COLUNA} hidden lg:table-cell`}>Último acesso</th>
            <th scope="col" className={`${CABECALHO_COLUNA} hidden lg:table-cell`}>Link</th>
            <th scope="col" className={`${CABECALHO_COLUNA} text-right`}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {itens.map((acesso) => (
            <tr key={acesso.id} data-testid={`acessos-portal-linha-${acesso.id}`} className="border-t border-border align-top">
              <th scope="row" className="min-w-0 break-words px-4 py-2 text-left font-normal">
                <span className="block break-words font-semibold">{acesso.nome}</span>
                <span className="block break-all text-[length:var(--font-label)] font-semibold text-muted-foreground">{acesso.email}</span>
                <span className="mt-1 block break-words text-[length:var(--font-label)] font-semibold text-muted-foreground lg:hidden">
                  Último acesso: {rotuloUltimoAcesso(acesso.ultimoAcessoEm)}
                </span>
                <span className="block break-words text-[length:var(--font-label)] font-semibold text-muted-foreground lg:hidden">
                  Link: {rotuloLinkAcesso(acesso, agora)}
                </span>
              </th>
              <td className="px-4 py-2">
                <SeloSituacao situacao={acesso.situacao} />
              </td>
              <td className="hidden break-words px-4 py-2 tabular-nums lg:table-cell">{rotuloUltimoAcesso(acesso.ultimoAcessoEm)}</td>
              <td className="hidden break-words px-4 py-2 tabular-nums lg:table-cell">{rotuloLinkAcesso(acesso, agora)}</td>
              <td className="px-4 py-2">
                <AcoesLinha acesso={acesso} agora={agora} ocupado={acaoEmAndamentoId === acesso.id} aoAcionar={aoAcionar} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AcessosPortalConteudo({
  cliente,
  busca = '',
  situacao = 'TODOS',
  lista,
  carregando = false,
  erro = null,
  agora = new Date(),
  acaoEmAndamentoId = null,
  aoAcionar,
  aoMudarCliente,
  aoMudarBusca,
  aoMudarSituacao,
  aoMudarPagina,
  aoConvidar,
  aoLimparFiltros,
  aoTentarNovamente,
}: AcessosPortalConteudoProps): ReactElement {
  const semCliente = cliente === null;
  const filtrosAtivos = busca.trim().length > 0 || situacao !== 'TODOS';
  const mudarCliente = aoMudarCliente ?? ((): void => undefined);
  return (
    <div data-testid="acessos-portal-page" className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-[length:var(--font-display)] font-semibold leading-tight">Acessos do portal</h1>
        <p className="text-muted-foreground">
          Gerencie quem acessa o portal de acompanhamento em cada cliente. Os links de acesso são entregues por você, por e-mail ou WhatsApp.
        </p>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end">
        <div className="flex flex-col gap-2 lg:w-80" data-testid="acessos-portal-cliente">
          <Label htmlFor="acessos-portal-cliente-seletor">Cliente</Label>
          <ClienteCombobox id="acessos-portal-cliente-seletor" value={cliente} onChange={mudarCliente} />
        </div>
        <div className="flex flex-col gap-2 lg:w-64">
          <Label htmlFor="acessos-portal-busca">Nome ou e-mail</Label>
          <Input
            id="acessos-portal-busca"
            type="search"
            value={busca}
            disabled={semCliente}
            onChange={(evento) => aoMudarBusca?.(evento.target.value)}
            data-testid="acessos-portal-busca"
          />
        </div>
        <div className="flex flex-col gap-2 lg:w-64">
          <Label htmlFor="acessos-portal-situacao">Situação</Label>
          <Select
            value={situacao}
            disabled={semCliente}
            onValueChange={(valor) => {
              const escolhida = FILTROS_SITUACAO.find((candidata) => candidata === valor);
              if (escolhida !== undefined) aoMudarSituacao?.(escolhida);
            }}
          >
            <SelectTrigger id="acessos-portal-situacao" data-testid="acessos-portal-situacao-filtro" className={`${CLASSE_ALVO} w-full`}>
              <SelectValue>{ROTULO_FILTRO_SITUACAO[situacao]}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {FILTROS_SITUACAO.map((filtro) => (
                <SelectItem key={filtro} value={filtro} className="min-h-11 lg:min-h-8">
                  {ROTULO_FILTRO_SITUACAO[filtro]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-2 lg:ml-auto">
          <BotaoConvidar desabilitado={semCliente} aoConvidar={aoConvidar} />
        </div>
      </div>

      {semCliente ? (
        <EstadoPagina icone={Building2} titulo="Escolha um cliente para ver os usuários do portal." />
      ) : erro === 'permissao' ? (
        <div role="alert" data-testid="acessos-portal-erro-permissao" className="flex items-start gap-2 py-8">
          <AlertCircle aria-hidden="true" className="mt-1 size-4 shrink-0 text-destructive" />
          <p>{MENSAGEM_SEM_PERMISSAO_TELA}</p>
        </div>
      ) : erro === 'carga' ? (
        <div role="alert" data-testid="acessos-portal-erro" className="flex flex-col items-start gap-4 py-8">
          <p className="flex items-start gap-2">
            <AlertCircle aria-hidden="true" className="mt-1 size-4 shrink-0 text-destructive" />
            {MENSAGEM_ERRO_CARGA}
          </p>
          <Button type="button" variant="outline" onClick={aoTentarNovamente} className={CLASSE_ALVO}>
            Tentar novamente
          </Button>
        </div>
      ) : carregando || lista === null ? (
        <ListaCarregando />
      ) : lista.itens.length === 0 ? (
        filtrosAtivos ? (
          <EstadoPagina icone={UserX} titulo="Nenhum usuário encontrado com estes filtros.">
            <Button type="button" variant="outline" onClick={aoLimparFiltros} className={CLASSE_ALVO}>
              Limpar filtros
            </Button>
          </EstadoPagina>
        ) : (
          <EstadoPagina
            icone={UserPlus}
            titulo="Este cliente ainda não tem usuários no portal."
            apoio="Convide a primeira pessoa para que ela acompanhe os pedidos."
          >
            <Button type="button" onClick={aoConvidar} className={CLASSE_ALVO}>
              <UserPlus aria-hidden="true" className="size-4" />
              Convidar usuário
            </Button>
          </EstadoPagina>
        )
      ) : (
        <div className="flex flex-col gap-4">
          <TabelaAcessos itens={lista.itens} agora={agora} acaoEmAndamentoId={acaoEmAndamentoId} aoAcionar={aoAcionar} />
          <div className="overflow-hidden rounded-xl border border-border">
            <TablePagination
              appearance="semantica"
              hidePageSize
              page={lista.pagina}
              pageSize={lista.tamanhoPagina}
              totalItems={lista.total}
              itemLabel="usuários"
              onPageChange={(pagina) => aoMudarPagina?.(pagina)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

interface ConfirmacaoPendente {
  readonly acao: AcaoConfirmavel;
  readonly acesso: AcessoPortalView;
}

interface LinkEmMemoria {
  readonly link: LinkPortalGerado;
  readonly nome: string;
  readonly senhaAtualContinua: boolean;
}

function clienteIdDaUrl(): string | null {
  const valor = new URLSearchParams(window.location.search).get('clienteId');
  return valor !== null && UUID.test(valor) ? valor : null;
}

function traduzirFalha(falha: unknown): ErroAcessoTraduzido {
  return falha instanceof ErroAcessoPortal ? mensagemErroAcesso(falha.status, falha.mensagemServidor) : mensagemErroAcesso(null);
}

export function AcessosPortalPage(): ReactElement {
  const toast = useToast();
  const [cliente, setCliente] = useState<ClienteLookupPv | null>(null);
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [situacao, setSituacao] = useState<FiltroSituacaoAcessoPortal>('TODOS');
  const [pagina, setPagina] = useState(1);
  const [lista, setLista] = useState<AcessoPortalLista | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<ErroTelaAcessos | null>(null);
  const [recarga, setRecarga] = useState(0);
  const [conviteAberto, setConviteAberto] = useState(false);
  const [linkEmMemoria, setLinkEmMemoria] = useState<LinkEmMemoria | null>(null);
  const [confirmacao, setConfirmacao] = useState<ConfirmacaoPendente | null>(null);
  const [enviandoConfirmacao, setEnviandoConfirmacao] = useState(false);
  const [erroConfirmacao, setErroConfirmacao] = useState<ErroAcessoTraduzido | null>(null);
  const [acaoEmAndamentoId, setAcaoEmAndamentoId] = useState<string | null>(null);

  const recarregar = useCallback((): void => setRecarga((n) => n + 1), []);

  // Cliente vindo do atalho do cadastro (?clienteId=): resolve o nome para o seletor.
  useEffect(() => {
    const idDaUrl = clienteIdDaUrl();
    if (idDaUrl === null) return;
    let vivo = true;
    void buscarClientePorId(idDaUrl).then((encontrado) => {
      if (vivo) setCliente(encontrado);
    });
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    const temporizador = window.setTimeout(() => {
      setBuscaAplicada(busca.trim());
      setPagina(1);
    }, ATRASO_BUSCA_MS);
    return () => window.clearTimeout(temporizador);
  }, [busca]);

  useEffect(() => {
    if (cliente === null) {
      setLista(null);
      return;
    }
    let vivo = true;
    setCarregando(true);
    setErro(null);
    listarAcessos({ clienteId: cliente.id, busca: buscaAplicada, situacao, pagina })
      .then((resposta) => {
        if (vivo) setLista(resposta);
      })
      .catch((falha: unknown) => {
        if (vivo) setErro(falha instanceof ErroAcessoPortal && falha.status === 403 ? 'permissao' : 'carga');
      })
      .finally(() => {
        if (vivo) setCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, [cliente, buscaAplicada, situacao, pagina, recarga]);

  // Página além do fim (a lista encolheu): volta para a primeira, sem mensagem de erro.
  const vazia = lista !== null && lista.itens.length === 0;
  useEffect(() => {
    if (vazia && pagina > 1) setPagina(1);
  }, [vazia, pagina]);

  const escolherCliente = (escolhido: ClienteLookupPv): void => {
    setCliente(escolhido);
    setPagina(1);
    window.history.replaceState(null, '', `${window.location.pathname}?clienteId=${encodeURIComponent(escolhido.id)}`);
  };

  const limparFiltros = (): void => {
    setBusca('');
    setBuscaAplicada('');
    setSituacao('TODOS');
    setPagina(1);
  };

  const mostrarLink = (link: LinkPortalGerado, nome: string, situacaoAntes: SituacaoAcessoPortal): void => {
    setLinkEmMemoria({ link, nome, senhaAtualContinua: situacaoAntes === 'ATIVO' && link.tipo === 'REDEFINICAO' });
  };

  /** Geração direta (sem confirmação): a falha vira toast e a linha não muda. */
  const gerarDireto = async (acesso: AcessoPortalView): Promise<void> => {
    setAcaoEmAndamentoId(acesso.id);
    try {
      const resposta = await gerarLink(acesso.id, acesso.versao);
      mostrarLink(resposta.link, acesso.nome, acesso.situacao);
      recarregar();
    } catch (falha) {
      const traduzido = traduzirFalha(falha);
      toast.error({ title: traduzido.mensagem, action: traduzido.recarregar ? { label: 'Recarregar', onClick: recarregar } : undefined });
    } finally {
      setAcaoEmAndamentoId(null);
    }
  };

  const acionar = (acao: IdAcaoAcesso, acesso: AcessoPortalView): void => {
    setErroConfirmacao(null);
    if (acao === 'gerar-link') {
      void gerarDireto(acesso);
    } else if (acao === 'gerar-novo-link') {
      // Com link vivo, gerar outro derruba o anterior: pede confirmação antes.
      if (linkVivo(acesso)) setConfirmacao({ acao, acesso });
      else void gerarDireto(acesso);
    } else {
      setConfirmacao({ acao, acesso });
    }
  };

  const fecharConfirmacao = (): void => {
    setConfirmacao(null);
    setErroConfirmacao(null);
  };

  const confirmar = async (): Promise<void> => {
    if (confirmacao === null) return;
    const { acao, acesso } = confirmacao;
    setEnviandoConfirmacao(true);
    setErroConfirmacao(null);
    try {
      if (acao === 'desativar') {
        await desativarAcesso(acesso.id, acesso.versao);
        toast.success({ title: 'Acesso desativado', description: `${acesso.nome} não consegue mais entrar no portal.` });
      } else if (acao === 'revogar-link') {
        await revogarLink(acesso.id, acesso.versao);
        toast.success({ title: 'Link revogado', description: `O link de ${acesso.nome} deixou de funcionar.` });
      } else if (acao === 'reativar') {
        // Reativar devolve o usuário como pendente, sem senha: o link de redefinição abre direto.
        const resposta = await reativarAcesso(acesso.id, acesso.versao);
        mostrarLink(resposta.link, acesso.nome, resposta.acesso.situacao);
        toast.success({ title: 'Acesso reativado', description: `Entregue o novo link para ${acesso.nome}.` });
      } else {
        const resposta = await gerarLink(acesso.id, acesso.versao);
        mostrarLink(resposta.link, acesso.nome, acesso.situacao);
      }
      fecharConfirmacao();
      recarregar();
    } catch (falha) {
      setErroConfirmacao(traduzirFalha(falha));
    } finally {
      setEnviandoConfirmacao(false);
    }
  };

  const aoCriado = (resposta: CriarAcessoPortalResposta): void => {
    setConviteAberto(false);
    mostrarLink(resposta.link, resposta.acesso.nome, resposta.acesso.situacao);
    recarregar();
  };

  return (
    <>
      <AcessosPortalConteudo
        cliente={cliente}
        busca={busca}
        situacao={situacao}
        lista={lista}
        carregando={carregando}
        erro={erro}
        acaoEmAndamentoId={acaoEmAndamentoId}
        aoAcionar={acionar}
        aoMudarCliente={escolherCliente}
        aoMudarBusca={setBusca}
        aoMudarSituacao={(proxima) => {
          setSituacao(proxima);
          setPagina(1);
        }}
        aoMudarPagina={setPagina}
        aoConvidar={() => setConviteAberto(true)}
        aoLimparFiltros={limparFiltros}
        aoTentarNovamente={recarregar}
      />
      <AcessoPortalDialog aberto={conviteAberto} cliente={cliente} aoFechar={() => setConviteAberto(false)} aoCriado={aoCriado} />
      <AcessoConfirmarDialog
        acao={confirmacao?.acao ?? null}
        acesso={confirmacao?.acesso ?? null}
        clienteNome={cliente?.nome ?? ''}
        enviando={enviandoConfirmacao}
        erro={erroConfirmacao}
        aoConfirmar={() => void confirmar()}
        aoCancelar={fecharConfirmacao}
        aoRecarregar={() => {
          fecharConfirmacao();
          recarregar();
        }}
      />
      <LinkGeradoDialog
        link={linkEmMemoria?.link ?? null}
        nome={linkEmMemoria?.nome ?? ''}
        senhaAtualContinua={linkEmMemoria?.senhaAtualContinua ?? false}
        aoFechar={() => setLinkEmMemoria(null)}
      />
    </>
  );
}
